import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid2,
  CircularProgress,
  TextField,
  InputAdornment,
  Link as MuiLink,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import config from "../common/Config";
import "../common/commonStyles.css";
import { postDashboardDatatable } from "../../utils/dashboardApi";

const PicklistGenerated = () => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const s = localStorage.getItem('startDate');
  const e = localStorage.getItem('endDate');

  const formatDate = (date) => {
    if (!date) return "0";
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  const getCurrentMonthDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const startDateFormatted = getCurrentMonthDate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { response, data: json } = await postDashboardDatatable(token, 'picking/list', {
        draw: String(page + 1),
        start: String(page * rowsPerPage),
        length: String(rowsPerPage),
        today: '1',
        search: { value: searchValue, regex: 'false' },
      });

      if (!response?.ok) {
        throw new Error(`Failed to fetch data: ${response?.status ?? 'unknown'}`);
      }

      if (json && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data.length);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, searchValue]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = "/react/login";
      return;
    }
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(0);
    }
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const formatRowDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <>
      <Box className="page-container" >
        <Paper
          className="paper-style"
        >
          <Box sx={{ px:2, py:1.2}}>
            <Grid2
              container
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid2 size={{ xs: 12, md: 2 }}>
                <Typography className="page-title">
                  Picklist Generated
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Box sx={{ gap: 1, display: 'flex', flexDirection: {'xs': 'column', 'md': 'row'}, justifyContent: 'space-between', alignItems: 'center' }}> 
                <TextField
                  className="input-border"
                  fullWidth
                  size="small"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify
                          icon="eva:search-fill"
                          sx={{ color: "#212b36", width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
   
                <Typography
                  align="right"
                  sx={{
                    color: "#212b36",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    whiteSpace: "nowrap",
                  }}
                >
                  From: {s ? formatDate(s) : startDateFormatted} &nbsp;
                  To: {e ? formatDate(e) : startDateFormatted}
                </Typography>
                </Box>
              </Grid2>
            </Grid2>
            </Box>
            <TableContainer
            >
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              )}
              <Table
                size="small"
                className="table-cell-border"
                >
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell
                      width="100px"
                    >
                      #
                    </TableCell>
                    <TableCell
                      width="100px"
                    >
                      Picklist No
                    </TableCell>
                    <TableCell
                      width="100px"
                    >
                      Invoices
                    </TableCell>
                    <TableCell
                      width="100px"
                    >
                      Route Name
                    </TableCell>
                    <TableCell
                      width="100px"
                    >
                      Picker
                    </TableCell>
                    <TableCell
                      width="100px"
                    >
                      Picklist Date
                    </TableCell>
                    <TableCell
                      width="100px"
                    >
                      Picking Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && data && data.length > 0 ? (
                    data.map((row, index) => (
                      <TableRow
                        key={index}
                        hover
                        className="table-cell-style"
                      >
                        <TableCell
                    
                        >
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell
                        >
                          <MuiLink
                            href={`${config.BASE_URL}/sales/view_picking_list/${row.picklist_number}`}
                            sx={{
                              color: '#212b36',
                              textDecoration: 'underline',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {row.picklist_number}
                          </MuiLink>
                        </TableCell>
                        <TableCell
                        >
                          {row.i}
                        </TableCell>
                        <TableCell

                        >
                          {row.route_name}
                        </TableCell>
                        <TableCell

                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                        >
                          {formatRowDate(row.print_on)}
                        </TableCell>
                        <TableCell
                        >
                          {row.picking_status}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : !loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{ py: 8 }}
                      >
                        <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                          No data found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            {!loading && (
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                className="pagination-styles"
              />
            )}
        </Paper>
      </Box>
    </>
  );
};

export default PicklistGenerated;
